import React, { useCallback, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useToast } from '@eluve/components';
import { toSectionStatus } from '@eluve/fhir-data';
import { useMedplum } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { TaskDetailDrawer } from './components/TaskDetailDrawer';
import { TasksCard } from './components/TasksCard';
import { setTaskDone, useFhirPatientId, usePatientTasks } from './data/tasks';

/**
 * The My-tasks vertical slice for the new patient profile Overview tab
 * (ENG-5368): the signed-in clinician's Clinical Tasks for this patient.
 * Renders nothing unless the tenant CLINICAL_TASKS flag AND the same-named
 * PostHog user flag are both on — Clinical Tasks is the card's only data
 * producer, and its UI hides itself behind these flags everywhere (same
 * gate as the appointment-side panel). The open drawer lives in the URL
 * (`?task=<id>`) so detail views stay deep-linkable.
 */
const DRAWER_PARAM = 'task';

export const MyTasksSection: React.FC = () => {
  const tenantFlagOn = useIsFeatureFlagEnabled('CLINICAL_TASKS');
  const userFlagOn = useIsPostHogFeatureFlagEnabled('CLINICAL_TASKS');

  if (!tenantFlagOn || !userFlagOn) return null;
  return <MyTasksSectionContent />;
};

const MyTasksSectionContent: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();
  const { client, practitionerId } = useMedplum();
  const toast = useToast();

  const fhirPatient = useFhirPatientId(patientId);
  const tasks = usePatientTasks(fhirPatient.data ?? null, practitionerId);

  const [togglingIds, setTogglingIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const toggle = useCallback(
    async (taskId: string, done: boolean) => {
      if (!client) return;
      setTogglingIds((current) => new Set(current).add(taskId));
      try {
        await setTaskDone(client, {
          taskId,
          done,
          practitionerReference: practitionerId
            ? `Practitioner/${practitionerId}`
            : null,
        });
        tasks.refetch();
      } catch {
        toast.error("Couldn't update the task.");
      } finally {
        setTogglingIds((current) => {
          const next = new Set(current);
          next.delete(taskId);
          return next;
        });
      }
    },
    [client, practitionerId, tasks, toast],
  );

  const openId = searchParams.get(DRAWER_PARAM);
  const openTask = (tasks.data ?? []).find((task) => task.id === openId);

  const openDrawer = useCallback(
    (taskId: string) => {
      setSearchParams(
        (params) => {
          // One profile drawer at a time — drop the to-dos card's param so a
          // shared/crafted URL can't stack both sheets.
          params.delete('serviceRequest');
          params.set(DRAWER_PARAM, taskId);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(DRAWER_PARAM);
        return params;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const status = toSectionStatus({
    loading: fhirPatient.loading || tasks.loading,
    data: tasks.data,
    readError: fhirPatient.readError ?? tasks.readError,
  });

  return (
    <>
      <TasksCard
        tasks={tasks.data ?? []}
        status={status}
        paging={tasks}
        now={new Date()}
        togglingIds={togglingIds}
        onToggle={(taskId, done) => void toggle(taskId, done)}
        onOpen={openDrawer}
      />
      <TaskDetailDrawer
        task={openTask}
        open={Boolean(openId && openTask)}
        onClose={closeDrawer}
        toggling={Boolean(openId && togglingIds.has(openId))}
        onToggle={(taskId, done) => void toggle(taskId, done)}
      />
    </>
  );
};
